import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import api from '../services/api'
import { useAuth } from './AuthContext'
import ConfirmModal from '../components/ConfirmModal'
import { Input } from '../components/ui'
import { formatRub } from '../utils/formatRub'

const EmployeeNotificationsContext = createContext(null)

async function fetchPayoutWishRequests () {
  try {
    const res = await api.get('/company/me/payout-wish-requests')
    return Array.isArray(res.data?.items) ? res.data.items : []
  } catch (_) {
    return []
  }
}

async function fetchPendingContractApprovals () {
  try {
    const res = await api.get('/company/me/pending-contract-approvals')
    return Array.isArray(res.data?.items) ? res.data.items : []
  } catch (_) {
    return []
  }
}

async function fetchPendingBudgetApprovals () {
  try {
    const res = await api.get('/company/me/pending-budget-approvals')
    return Array.isArray(res.data?.items) ? res.data.items : []
  } catch (_) {
    return []
  }
}

export function EmployeeNotificationsProvider ({ children }) {
  const { user } = useAuth()
  const location = useLocation()
  const employeeLinked = Boolean(user?.employee_id)

  const [payoutRequests, setPayoutRequests] = useState([])
  const [pendingContractApprovals, setPendingContractApprovals] = useState([])
  const [pendingBudgetApprovals, setPendingBudgetApprovals] = useState([])
  const [payoutModalOpen, setPayoutModalOpen] = useState(false)
  const [payoutModalReportId, setPayoutModalReportId] = useState(null)
  const [payoutAmount, setPayoutAmount] = useState('')
  const [payoutSubmitting, setPayoutSubmitting] = useState(false)
  const [payoutError, setPayoutError] = useState('')

  const refreshPayoutRequests = useCallback(async () => {
    if (!employeeLinked) {
      setPayoutRequests([])
      return []
    }
    const items = await fetchPayoutWishRequests()
    setPayoutRequests(items)
    return items
  }, [employeeLinked])

  const refreshPendingContractApprovals = useCallback(async () => {
    if (!employeeLinked) {
      setPendingContractApprovals([])
      return []
    }
    const items = await fetchPendingContractApprovals()
    setPendingContractApprovals(items)
    return items
  }, [employeeLinked])

  const refreshPendingBudgetApprovals = useCallback(async () => {
    if (!employeeLinked) {
      setPendingBudgetApprovals([])
      return []
    }
    const items = await fetchPendingBudgetApprovals()
    setPendingBudgetApprovals(items)
    return items
  }, [employeeLinked])

  const refreshPendingApprovals = useCallback(() => Promise.all([
    refreshPendingContractApprovals(),
    refreshPendingBudgetApprovals(),
  ]), [refreshPendingContractApprovals, refreshPendingBudgetApprovals])

  useEffect(() => {
    if (!employeeLinked) {
      setPayoutRequests([])
      setPendingContractApprovals([])
      setPendingBudgetApprovals([])
      return undefined
    }
    let cancelled = false
    Promise.all([
      fetchPayoutWishRequests(),
      fetchPendingContractApprovals(),
      fetchPendingBudgetApprovals(),
    ]).then(([payoutItems, contractItems, budgetItems]) => {
      if (cancelled) return
      setPayoutRequests(payoutItems)
      setPendingContractApprovals(contractItems)
      setPendingBudgetApprovals(budgetItems)
    })
    return () => { cancelled = true }
  }, [employeeLinked, location.pathname])

  const pendingPayoutRequest = useMemo(
    () => payoutRequests.find((req) => req.wish_amount === null || req.wish_amount === undefined) || null,
    [payoutRequests]
  )

  const payoutModalRequest = payoutRequests.find((req) => req.report_id === payoutModalReportId) || null

  const openPayoutModal = useCallback((req) => {
    if (!req) return
    setPayoutModalReportId(req.report_id)
    setPayoutAmount(req.wish_amount === null || req.wish_amount === undefined ? '' : String(req.wish_amount))
    setPayoutError('')
    setPayoutModalOpen(true)
  }, [])

  const submitPayoutWish = async () => {
    if (!payoutModalReportId || !payoutModalRequest) return
    const earned = Number(payoutModalRequest.earned || 0)
    const amount = Math.round(Number(String(payoutAmount).replace(/\s/g, '').replace(',', '.')))
    if (!Number.isFinite(amount) || amount < 0) {
      setPayoutError('Введите корректную сумму')
      return
    }
    if (amount > earned) {
      setPayoutError(`Сумма не может превышать заработанное за квартал (${formatRub(earned, { showZero: true })})`)
      return
    }
    setPayoutSubmitting(true)
    setPayoutError('')
    try {
      await api.post(`/company/reports/${payoutModalReportId}/payout-wishes`, { amount })
      await refreshPayoutRequests()
      setPayoutModalOpen(false)
    } catch (err) {
      setPayoutError(err.response?.data?.error || 'Не удалось отправить пожелание')
    } finally {
      setPayoutSubmitting(false)
    }
  }

  const value = useMemo(() => ({
    payoutRequests,
    pendingPayoutRequest,
    pendingContractApprovals,
    pendingBudgetApprovals,
    refreshPayoutRequests,
    refreshPendingContractApprovals,
    refreshPendingBudgetApprovals,
    refreshPendingApprovals,
    openPayoutModal,
  }), [
    payoutRequests,
    pendingPayoutRequest,
    pendingContractApprovals,
    pendingBudgetApprovals,
    refreshPayoutRequests,
    refreshPendingContractApprovals,
    refreshPendingBudgetApprovals,
    refreshPendingApprovals,
    openPayoutModal,
  ])

  return (
    <EmployeeNotificationsContext.Provider value={value}>
      {children}
      {payoutModalOpen && payoutModalRequest && (
        <ConfirmModal
          title="Сколько вы хотите получить?"
          message={(
            <div className="payout-wish-modal">
              <p>
                За {payoutModalRequest.period_label} вы заработали{' '}
                <strong>{formatRub(payoutModalRequest.earned, { showZero: true })}</strong>. Укажите сумму к получению — не больше заработанного за квартал.
              </p>
              <Input
                type="number"
                min="0"
                max={payoutModalRequest.earned}
                value={payoutAmount}
                onChange={(event) => setPayoutAmount(event.target.value)}
                placeholder="Сумма к получению"
                aria-label="Сумма к получению"
              />
            </div>
          )}
          confirmLabel={payoutSubmitting ? 'Отправка…' : 'Отправить'}
          cancelLabel="Отмена"
          confirmVariant="primary"
          confirmDisabled={payoutSubmitting}
          error={payoutError}
          onConfirm={submitPayoutWish}
          onCancel={() => setPayoutModalOpen(false)}
        />
      )}
    </EmployeeNotificationsContext.Provider>
  )
}

export function useEmployeeNotifications () {
  const ctx = useContext(EmployeeNotificationsContext)
  if (!ctx) {
    throw new Error('useEmployeeNotifications must be used within EmployeeNotificationsProvider')
  }
  return ctx
}

export function useEmployeeNotificationsOptional () {
  return useContext(EmployeeNotificationsContext)
}
